import React, { memo, useState, useEffect } from 'react';
import { sprintf, __ } from '@wordpress/i18n';
import { useSelector, useDispatch } from 'react-redux';
import { decodeEntities } from '@wordpress/html-entities';
import { Grid } from '@brainstormforce/starter-templates-components';
import {
	useFilteredSites,
	useSitesBySearchTerm,
	useSitesBySiteType,
} from '../../util/helper-functions';
import Pagination from '../pagination';
import ICONS from '../icons';

import './style.scss';

const SitesGrid = ( { sitesData, column } ) => {
	let sites = { ...sitesData };
	const {
		filtersortby,
		searchTerm,
		customizerParam,
		showMore,
		enabledPagination,
		currentPage,
		pageSize,
		pattern,
		enabledShowExcerpt,
	} = useSelector( ( state ) => state );
	const dispatch = useDispatch();
	const [ showMoreCount, setShowMoreCount ] = useState( false );
	const filteredSites = useFilteredSites();
	// Filter site by site type. Ex. premium, free.
	sites = useSitesBySiteType( sitesData );

	// For dynamic search result.
	sites = useSitesBySearchTerm( sites );
	const allSites = [];
	if ( Object.keys( sites ).length ) {
		for ( const siteId in sites ) {
			let builders = ICONS.elementor;
			builders +=
				sites[ siteId ][ 'related-bb-template' ] !== ''
					? ICONS.beaverbuilder
					: '';
			builders +=
				sites[ siteId ][ 'related-gutenberg-template' ] !== ''
					? ICONS.blockeditor
					: '';
			allSites.push( {
				id: sites[ siteId ].id,
				image: '' !== sites[ siteId ][ 'seo-thumbnail-url' ] ? sites[ siteId ][ 'seo-thumbnail-url' ] : sites[ siteId ][ 'thumbnail-image-url' ],
				title: decodeEntities( sites[ siteId ].title ),
				desc: enabledShowExcerpt ? sites[ siteId ][ 'post-excerpt' ] : '',
				pageBuilder: `<span> Available In: </span><span> ${ builders } </span>`,
				link: 'pattern2' === pattern ? '#' : sites[ siteId ][ 'astra-site-url' ] + customizerParam,
				livelink:
					sites[ siteId ][ 'astra-site-live-url' ] + AstraShowcaseVars.customizerParam,
				badge: ( () => {
					let badge = '';
					if ( sites[ siteId ][ 'astra-sites-type' ] === 'signature' ) {
						badge =
							<div className="stc-grid-item-badge-wrapper">
								<div className="stc-grid-item-badge">{ ICONS.signature }</div>
								<div className={ `stc-grid-item-badge-tooltip ${ sites[ siteId ][ 'astra-sites-type' ] }` }>
									{ __( 'Signature', 'astra-sites-showcase' ) }
								</div>
							</div>;
					} else if ( sites[ siteId ][ 'astra-sites-type' ] !== 'free' ) {
						badge =
							<div className="stc-grid-item-badge-wrapper">
								<div className="stc-grid-item-badge">{ ICONS.premium }</div>
								<div className="stc-grid-item-badge-tooltip">
									{ __( 'Premium', 'astra-sites-showcase' ) }
								</div>
							</div>;
					}
					return badge;
				} )(),
				...sites[ siteId ],
			} );
		}
	}

	if ( filtersortby === 'latest' && allSites.length ) {
		allSites.sort(
			( siteA, siteB ) => siteB[ 'publish-date' ] - siteA[ 'publish-date' ]
		);
	}

	const defaultSites = [];

	if ( searchTerm ) {
		const relatedSites = {
			multipurpose: {},
			top20: {},
		};

		for ( const siteId in filteredSites ) {
			const site = filteredSites[ siteId ];
			if ( Object.values( site.categories ).includes( 'multipurpose' ) ) {
				relatedSites.multipurpose[ siteId ] = site;
			} else if ( Object.keys( relatedSites.top20 ).length <= 20 ) {
				relatedSites.top20[ siteId ] = site;
			}
		}

		const allRelatedSites = Object.assign(
			relatedSites.multipurpose,
			relatedSites.top20
		);

		for ( const siteId in allRelatedSites ) {
			let builders = ICONS.elementor;
			builders +=
				allRelatedSites[ siteId ][ 'related-bb-template' ] !== ''
					? ICONS.beaverbuilder
					: '';
			builders +=
				allRelatedSites[ siteId ][ 'related-gutenberg-template' ] !== ''
					? ICONS.blockeditor
					: '';
			defaultSites.push( {
				id: allRelatedSites[ siteId ].id,
				image: allRelatedSites[ siteId ][ 'thumbnail-image-url' ],
				title: decodeEntities( allRelatedSites[ siteId ].title ),
				desc: enabledShowExcerpt
					? allRelatedSites[ siteId ][ 'post-excerpt' ]
					: '',
				pageBuilder: `<span> Available In: </span><span> ${ builders } </span>`,
				link: allRelatedSites[ siteId ][ 'astra-site-url' ],
				livelink: allRelatedSites[ siteId ][ 'astra-site-live-url' ],
				badge: ( () => {
					let badge = ( <div className="stc-item-badge-free">Free</div> );
					if ( allRelatedSites[ siteId ][ 'astra-sites-type' ] === 'signature' ) {
						badge = (
							<div className="stc-item-badge-premium">Signature</div>
						);
					} else if ( allRelatedSites[ siteId ][ 'astra-sites-type' ] !== 'free' ) {
						badge = <div className="stc-item-badge-free">Premium</div>;
					}
					return badge;
				} )(),
				...allRelatedSites[ siteId ],
			} );
		}
	}

	const handleShowMoreClick = () => {
		dispatch( {
			type: 'set',
			showMore: true,
		} );
	};

	useEffect( () => {
		const showMoreVal = document
			.getElementById( 'astra-sites-grid' )
			.getAttribute( 'data-show-more' );

		if ( showMoreVal !== 'false' ) {
			setShowMoreCount( parseInt( showMoreVal ) );
		}
	}, [] );

	let initialSites = [];
	if ( !! showMoreCount ) {
		initialSites = allSites.slice( 0, showMoreCount );
	} else if ( !! enabledPagination ) {
		initialSites = allSites.slice(
			( currentPage - 1 ) * pageSize,
			currentPage * pageSize
		);
	} else {
		initialSites = allSites;
	}
	return (
		<div className="st-showcase-templates-content">
			<div className="st-sites-grid">
				{ searchTerm && allSites.length ? (
					<div className="st-sites-found-message">
						{ sprintf(
							/* translators: %1$s: search term. */
							__(
								'Starter Templates for "%1$s"',
								'astra-sites-showcase'
							),
							decodeEntities( searchTerm )
						) }
					</div>
				) : null }
				{ searchTerm && ! Object.keys( sites ).length ? (
					<div className="st-sites-found-message">
						{ sprintf(
							/* translators: %1$s is the search title. */
							__(
								'Other suggested Starter Templates for "%1$s"',
								'astra-sites-showcase'
							),
							decodeEntities( searchTerm )
						) }
					</div>
				) : null }

				{ allSites.length ? (
					<>
						<Grid
							column={ column }
							options={ initialSites }
							buttonLabel={ __( 'Details', 'astra-sites-showcase' ) }
							livePreview={ __(
								'Live Preview',
								'astra-sites-showcase'
							) }
							onClick={ ( event, site ) => {
								window.open( site.link, '_self' );
							} }
							enableNewUi={ true }
							className={ 'stc-grid-new-item' }
						/>
						{ ! showMore && !! showMoreCount && (
							<div className="st-show-more-wrapper">
								<button
									id="st-grid-show-more-btn"
									className="st-grid-show-more-btn"
									onClick={ handleShowMoreClick }
								>
									{ __(
										'View All Templates',
										'astra-sites-showcase'
									) }
								</button>
							</div>
						) }
						{ !! showMore && (
							<Grid
								className="st-show-more-grid"
								column={ column }
								options={ allSites.slice( showMoreCount ) }
								livePreview={ __(
									'Live Preview',
									'astra-sites-showcase'
								) }
								buttonLabel={ __(
									'View Full Site',
									'astra-sites-showcase'
								) }
								onClick={ ( event, site ) => {
									window.open( site.link, '_blank' );
								} }
							/>
						) }
						{ enabledPagination &&
							Object.keys( allSites ).length > pageSize && (
							<Pagination
								totalCount={ allSites.length }
								itemsPerPage={ pageSize }
								currentPage={ currentPage }
								siblings={ 1 }
							/>
						) }
					</>
				) : (
					<Grid
						column={ column }
						options={ defaultSites }
						buttonLabel={ __( 'Details', 'astra-sites-showcase' ) }
						livePreview={ __( 'Live Preview', 'astra-sites-showcase' ) }
						onClick={ ( event, site ) => {
							window.open( site.link, '_self' );
						} }
						enableNewUi={ true }
						className={ 'stc-grid-new-item' }
					/>
				) }
			</div>
		</div>
	);
};

export default memo( SitesGrid );
